import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, deleteDoc, doc, Timestamp, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useApp } from '../App';
import { requireRole } from '../utils/roles';

export default function BoardPanel() {
  const { currentTeam, isStaff, roles, userProfile } = useApp();
  const canEdit = isStaff;   // 공지 쓰기·삭제 = 운영진 (W1-01)
  const [tab, setTab] = useState('notices');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (currentTeam) loadItems(); }, [currentTeam, tab]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'teams', currentTeam.id, tab), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    if (tab === 'notices' && !requireRole(roles, 'isStaff')) return;
    e.preventDefault();
    if (!form.title) return;
    try {
      await addDoc(collection(db, 'teams', currentTeam.id, tab), {
        teamId: currentTeam.id,
        authorId: auth.currentUser.uid,
        authorName: userProfile?.name || auth.currentUser.email,
        title: form.title,
        content: form.content,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      setForm({ title: '', content: '' });
      setShowForm(false);
      loadItems();
    } catch (err) { alert('등록 실패: ' + err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'teams', currentTeam.id, tab, id));
      setItems(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) { alert('삭제 실패'); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 1200 }}>
      <div style={s.panel}>
        <div style={s.panelHeader}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['notices', '📢 공지'], ['posts', '💬 자유게시판']].map(([key, label]) => (
              <button key={key} onClick={() => { setTab(key); setSelected(null); }}
                style={{ ...s.tabBtn, backgroundColor: tab === key ? 'rgba(91,196,216,0.15)' : 'transparent', color: tab === key ? '#5BC4D8' : '#667788' }}>
                {label}
              </button>
            ))}
          </div>
          {(canEdit || tab === 'posts') && (
            <button onClick={() => setShowForm(!showForm)} style={s.addBtn}>+ 글쓰기</button>
          )}
        </div>
        <div style={s.panelBody}>
          {showForm && (
            <form onSubmit={handleSubmit} style={{ padding: 12, backgroundColor: 'rgba(91,196,216,0.03)', borderRadius: 8, marginBottom: 12 }}>
              <input style={s.input} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="제목" required />
              <textarea style={{ ...s.input, minHeight: 80, marginTop: 8, resize: 'vertical' }} value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="내용" />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="submit" style={s.submitBtn}>등록</button>
                <button type="button" onClick={() => setShowForm(false)} style={s.cancelBtn}>취소</button>
              </div>
            </form>
          )}
          {loading ? <div style={s.empty}>로딩 중...</div> :
            items.length === 0 ? <div style={s.empty}>게시글이 없습니다</div> :
            items.map(item => (
              <div key={item.id} onClick={() => setSelected(item)} style={{
                padding: '12px 14px', marginBottom: 4, borderRadius: 8, cursor: 'pointer',
                backgroundColor: selected?.id === item.id ? 'rgba(91,196,216,0.08)' : 'rgba(255,255,255,0.02)',
              }}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{item.title}</div>
                <div style={{ fontSize: 11, color: '#778899', marginTop: 4 }}>
                  {item.authorName || '익명'} · {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString('ko-KR') : ''}
                </div>
              </div>
            ))}
        </div>
      </div>

      <div style={s.panel}>
        <div style={s.panelHeader}><h2 style={s.panelTitle}>📄 내용</h2></div>
        <div style={s.panelBody}>
          {selected ? (
            <div style={{ padding: 12 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600, color: '#E8ECF1' }}>{selected.title}</h3>
              <div style={{ fontSize: 12, color: '#778899', marginBottom: 20 }}>
                {selected.authorName} · {selected.createdAt?.toDate ? selected.createdAt.toDate().toLocaleDateString('ko-KR') : ''}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.8, color: '#AAB4C0', whiteSpace: 'pre-wrap' }}>{selected.content}</div>
              {(canEdit || selected.authorId === auth.currentUser?.uid) && (
                <button onClick={() => handleDelete(selected.id)} style={{ marginTop: 24, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(248,113,113,0.3)', backgroundColor: 'transparent', color: '#F87171', fontSize: 12, cursor: 'pointer' }}>삭제</button>
              )}
            </div>
          ) : (
            <div style={s.empty}><div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>👈</div>게시글을 선택하세요</div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  panelHeader: { padding: '14px 20px', borderBottom: '1px solid rgba(91,196,216,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  panelTitle: { margin: 0, fontSize: 15, fontWeight: 600 },
  panelBody: { padding: 12, maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#556677', fontSize: 14 },
  tabBtn: { padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  addBtn: { padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.1)', fontSize: 14, backgroundColor: 'rgba(10,18,32,0.8)', color: '#E8ECF1', boxSizing: 'border-box' },
  submitBtn: { padding: '8px 20px', borderRadius: 6, border: 'none', backgroundColor: '#5BC4D8', color: '#0A1220', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  cancelBtn: { padding: '8px 16px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#8899AA', fontSize: 12, cursor: 'pointer' },
};
